import React, { useContext, useState } from "react";

import Checkbox from "@material-ui/core/Checkbox";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { Accordion, AccordionSummary, Collapse, NumberInput, SelectInput, MenuItem } from "@portex-pro/ui-components";
import { ButtonGroup } from "app/pages/quote-request/_components";
import { ShipmentPackagingType, ShipmentPalletType, ShipmentWeightUnit } from "app/pages/shipments/types/domain";
import classNames from "classnames";
import clsx from "clsx";
import { ResourceKey } from "i18next";
import { useTranslation } from "react-i18next";

import { CargoGroupType, NormalizedCargoGroup } from "../api/types";
import { CargoGroupContext } from "../CargoGroupProvider";
import { normalizedCargoGroupSelectors, selectManyCargoGroups } from "../store/cargoGroupsSlice";
import { useCargoGroupsSliceSelector } from "../store/cargoGroupsStore";

export const palletDimensions: Record<ShipmentPalletType, { length: number; width: number }> = {
  PALLET_48_40: { length: 48, width: 40 },
  PALLET_42_42: { length: 42, width: 42 },
  PALLET_48_48: { length: 48, width: 48 },
  PALLET_40_48: { length: 40, width: 48 },
  PALLET_48_42: { length: 48, width: 42 },
  PALLET_40_40: { length: 40, width: 40 },
  PALLET_48_45: { length: 48, width: 45 },
  PALLET_44_44: { length: 44, width: 44 },
  PALLET_36_36: { length: 36, width: 36 },
  PALLET_48_36: { length: 48, width: 36 },
  PALLET_45_455: { length: 45.5, width: 45.5 },
  PALLET_48_20: { length: 48, width: 20 },
  PALLET_NOT_SPECIFIED: { length: 0, width: 0 },
};

const palletTypeOptions = Object.values(ShipmentPalletType);
const packingTypeOptions = Object.values(ShipmentPackagingType).filter(
  (type) => !type.toString().startsWith("PALLETS")
);

export interface PackageGroupsViewProps {
  expanded: number | false;
  onExpandChange: (index: number | false) => void;
  divProps?: React.HTMLAttributes<HTMLDivElement>;
  sectionProps?: React.HTMLAttributes<HTMLElement>;
}

export const getPalletDimensionLabel = (palletType?: ShipmentPalletType): string => {
  if (!palletType) return "";
  const dims = palletDimensions[palletType];
  if (dims) return `(${dims.length}" x ${dims.width}")`;
  return "";
};

interface HandlingUnitViewProps {
  handlingUnit: NormalizedCargoGroup;
  index: number;
  total: number;
  expanded: number | false;

  handleAccordionChange: (idx: number) => void;
}

const HandlingUnitView: React.FC<HandlingUnitViewProps> = (props) => {
  const { handlingUnit, index, total, expanded, handleAccordionChange } = props;
  const { t } = useTranslation(["common", "shipper"]);
  const { updateCargoGroup, addCargoGroup, removeCargoGroup } = useContext(CargoGroupContext);

  const id = handlingUnit.id;
  const handlingUnitPackingType = handlingUnit.packaging_type;
  const isPalletized = handlingUnitPackingType.includes("PALLETS");

  // For FTL we will have only only packing unit per handling unit
  const packingUnitId = handlingUnit.packing_units?.[0];
  const packingUnit = useCargoGroupsSliceSelector((state) => {
    if (packingUnitId) {
      return normalizedCargoGroupSelectors.selectById(
        state.cargoGroupsSlice.normalizedCargoGroups,
        packingUnitId ?? -1
      );
    }
    return undefined;
  });

  const packingCount = packingUnit?.count ?? 0;
  const packingType = packingUnit?.packaging_type;

  const handlingUnitCount = handlingUnit.count;
  const isStackable = handlingUnit.is_stackable;
  const length = handlingUnit.length_per_count;
  const width = handlingUnit.width_per_count;
  const height = handlingUnit.height_per_count;
  const weightPerPackage = handlingUnit.weight_per_count;
  const weightUnit = handlingUnit.weight_unit;

  const caseCount = handlingUnit.case_count;

  const [weightType, setWeightType] = React.useState<"total" | "perItem">("perItem");
  const [palletType, setPalletType] = useState<ShipmentPalletType | undefined>(undefined);

  const palletDimLabel = getPalletDimensionLabel(palletType);

  const handleChangeIsPalletized = (isPalletized: boolean) => {
    if (isPalletized) {
      updateCargoGroup(id, {
        packaging_type: ShipmentPackagingType.Pallets,
      });
      addCargoGroup({
        handling_unit_id: id,
        cargo_group_type: CargoGroupType.PACKING,
        packaging_type: handlingUnit.packaging_type ?? ShipmentPackagingType.Bags,
        count: handlingUnit.count ?? undefined,
      });
    } else {
      updateCargoGroup(id, {
        packaging_type: packingUnit?.packaging_type ?? ShipmentPackagingType.Other,
        count: packingUnit?.count,
      });
      if (packingUnitId) {
        removeCargoGroup(packingUnitId);
      }
    }
  };

  const handleCountChange = (count: number) => {
    if (weightType === "total") {
      const newWeightPerCount = weightPerPackage
        ? (weightPerPackage * (handlingUnitCount ?? 0)) / (count || 1)
        : undefined;

      updateCargoGroup(id, {
        count,
        weight_per_count: newWeightPerCount,
      });
    } else {
      updateCargoGroup(id, { count });
    }
  };

  return (
    <div
      className={clsx(
        "w-full border border-gray-200",
        index === 0 ? "rounded-t-xl" : "",
        index === total - 1 ? "" : ""
      )}
      key={id}
    >
      <div
        className="flex flex-row items-center justify-between px-5 py-4 border-b cursor-pointer"
        onClick={() => handleAccordionChange(index)}
      >
        <span className="font-semibold text-base">
          {t("common:item")} {index + 1}
        </span>
        <ExpandMoreIcon
          className="transition-transform duration-200 ml-2"
          style={{ transform: expanded === index ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </div>

      <Collapse in={expanded === index}>
        <div className="flex flex-col gap-4 px-5 py-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[33.3333%]">
              <label className="block text-sm font-bold mb-1">{t("shipper:packingMethod")}</label>
              <ButtonGroup
                options={[
                  { value: "true", label: t("shipper:palletized") },
                  { value: "false", label: t("shipper:floorLoaded") },
                ]}
                selected={isPalletized ? "true" : "false"}
                onSelect={(value) => handleChangeIsPalletized(value === "true")}
              />
            </div>

            {isPalletized ? (
              <>
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm font-bold mb-1">{t("shipper:palletType_label")}</label>
                  <SelectInput
                    value={palletType || ""}
                    onChange={(event) => {
                      const { length, width } = palletDimensions[event.target.value as ShipmentPalletType];
                      setPalletType(event.target.value as ShipmentPalletType);
                      updateCargoGroup(id, {
                        width_per_count: width,
                        length_per_count: length,
                      });
                    }}
                    displayEmpty
                    fullWidth
                  >
                    <MenuItem value="" disabled>
                      Set Pallet Type
                    </MenuItem>
                    {palletTypeOptions.map((type) => (
                      <MenuItem key={type} value={type}>
                        {t(`shipper:palletTypeMap.${type}` as ResourceKey)}
                      </MenuItem>
                    ))}
                  </SelectInput>
                </div>

                <div className="flex-1 min-w-[150px]">
                  <label className="block text-sm font-bold mb-1">{t("shipper:palletCount")}</label>
                  <NumberInput
                    translate="no"
                    value={handlingUnitCount || undefined}
                    onChange={(e) => handleCountChange(e.target.value ? Number(e.target.value) : 0)}
                    fullWidth
                    onlyValidInput
                    disableError
                    displayZero
                    disableEmpty={false}
                    validator="Integer"
                  />
                </div>
              </>
            ) : (
              <>
                <div className="flex-1 min-w-[180px]">
                  <label className="block text-sm font-bold mb-1">{t("shipper:packingType")}</label>
                  <SelectInput
                    value={handlingUnitPackingType || ""}
                    onChange={(event) =>
                      updateCargoGroup(id, {
                        packaging_type: (event.target.value as ShipmentPackagingType) || undefined,
                      })
                    }
                    displayEmpty
                    fullWidth
                  >
                    <MenuItem value="">{t("shipper:setPackingType")}</MenuItem>
                    {packingTypeOptions.map((type) => (
                      <MenuItem key={type} value={type}>
                        {t(`shipper:packingTypeMap.${type}` as ResourceKey)}
                      </MenuItem>
                    ))}
                  </SelectInput>
                </div>

                <div className="flex-1 min-w-[150px]">
                  <label className="block text-sm font-bold mb-1">{t("shipper:packingCount")}</label>
                  <NumberInput
                    translate="no"
                    value={handlingUnitCount ?? undefined}
                    onChange={(e) =>
                      updateCargoGroup(id, {
                        count: e.target.value ? Number(e.target.value) : undefined,
                      })
                    }
                    fullWidth
                    onlyValidInput
                    disableError
                    validator="PositiveInt"
                  />
                </div>
              </>
            )}
          </div>

          {isPalletized && (
            <>
              <div className="flex">
                <button
                  type="button"
                  className="flex items-center focus:outline-none"
                  onClick={() => updateCargoGroup(id, { is_stackable: !isStackable })}
                  aria-pressed={!!isStackable}
                >
                  <Checkbox checked={!!isStackable} color="primary" />
                  <span className="text-base font-normal text-gray-900 select-none">
                    {t("shipper:packageGroupView.stackable", {
                      type: `Pallets ${palletDimLabel}`,
                    })}
                  </span>
                </button>
              </div>

              <div>
                <label className="block text-sm font-bold mb-1">
                  {t("shipper:dimensions")} ({t("common:in")})
                </label>
                <div className="flex items-center gap-2 max-w-[400px]">
                  <NumberInput
                    translate="no"
                    value={length ?? undefined}
                    onChange={(e) =>
                      updateCargoGroup(id, { length_per_count: e.target.value ? Number(e.target.value) : undefined })
                    }
                    placeholder="L"
                    onlyValidInput
                    disableError
                    validator="PositiveFloat"
                    style={{ width: 80 }}
                  />
                  <span className="text-lg font-medium">x</span>
                  <NumberInput
                    translate="no"
                    value={width ?? undefined}
                    onChange={(e) =>
                      updateCargoGroup(id, { width_per_count: e.target.value ? Number(e.target.value) : undefined })
                    }
                    placeholder="W"
                    onlyValidInput
                    disableError
                    validator="PositiveFloat"
                    style={{ width: 80 }}
                  />
                  <span className="text-lg font-medium">x</span>
                  <NumberInput
                    translate="no"
                    value={height ?? undefined}
                    onChange={(e) =>
                      updateCargoGroup(id, { height_per_count: e.target.value ? Number(e.target.value) : undefined })
                    }
                    placeholder="H"
                    onlyValidInput
                    disableError
                    validator="PositiveFloat"
                    style={{ width: 80 }}
                  />
                </div>
              </div>
            </>
          )}

          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-bold mb-1">Weight Type</label>
              <ButtonGroup
                options={[
                  { value: "total", label: t("common:total") },
                  { value: "perItem", label: "Per Item" },
                ]}
                selected={weightType}
                onSelect={(value) => setWeightType(value as "total" | "perItem")}
              />
            </div>

            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-bold mb-1">
                {weightType === "total"
                  ? t("shipper:loadAttributesStep.totalShipmentWeight")
                  : `Weight Per Item (${(weightUnit ?? "LB").toLowerCase()})`}
              </label>
              <NumberInput
                translate="no"
                value={
                  (weightType === "perItem" ? weightPerPackage : (weightPerPackage ?? 0) * (handlingUnitCount ?? 0)) ??
                  undefined
                }
                onChange={(e) => {
                  const weightValue = Number(e.target.value);

                  const count = isPalletized ? handlingUnitCount || 1 : packingCount || 1;

                  const perCountWeight = weightType === "total" ? weightValue / count : weightValue;

                  updateCargoGroup(id, {
                    weight_per_count: e.target.value ? perCountWeight : undefined,
                  });
                }}
                fullWidth
                onlyValidInput
                disableError
                validator="PositiveFloat"
              />
            </div>

            <div className="flex-1">
              <label className="block text-sm font-bold mb-1">Weight Unit</label>
              <ButtonGroup
                options={[
                  { value: "LB", label: t("common:lb") },
                  { value: "KG", label: t("common:kg") },
                ]}
                selected={weightUnit ?? "LB"}
                onSelect={(value) => updateCargoGroup(id, { weight_unit: value as ShipmentWeightUnit })}
              />
            </div>
          </div>
          {isPalletized && (
            <div className="flex flex-row flex-wrap gap-4">
              <div className="flex-1 min-w-[180px]">
                <label className="block text-sm font-bold mb-1">{t("shipper:packingType")}</label>
                <SelectInput
                  value={packingType || ""}
                  onChange={(event) => {
                    if (!packingUnitId && event.target.value !== "") {
                      addCargoGroup({
                        handling_unit_id: id,
                        cargo_group_type: CargoGroupType.PACKING,
                        packaging_type: event.target.value as ShipmentPackagingType,
                        count: 0,
                      });
                      return;
                    }

                    if (packingUnitId && event.target.value === "") {
                      removeCargoGroup(packingUnitId);
                      return;
                    }

                    if (!!packingUnitId) {
                      updateCargoGroup(packingUnitId, {
                        packaging_type: (event.target.value as ShipmentPackagingType) || undefined,
                      });
                    }
                  }}
                  displayEmpty
                  fullWidth
                >
                  <MenuItem value="">{t("shipper:setPackingType")}</MenuItem>
                  {packingTypeOptions.map((type) => (
                    <MenuItem key={type} value={type}>
                      {t(`shipper:packingTypeMap.${type}` as ResourceKey)}
                    </MenuItem>
                  ))}
                </SelectInput>
              </div>

              <div className="flex-1 min-w-[150px]">
                <label className="block text-sm font-bold mb-1">{t("shipper:packingCount")}</label>
                <NumberInput
                  translate="no"
                  value={packingCount || undefined}
                  onChange={(e) => {
                    if (!packingUnitId) {
                      addCargoGroup({
                        handling_unit_id: id,
                        cargo_group_type: CargoGroupType.PACKING,
                        packaging_type: ShipmentPackagingType.Other,
                        count: e.target.value ? Number(e.target.value) : undefined,
                      });
                      return;
                    } else {
                      updateCargoGroup(packingUnitId, {
                        count: e.target.value ? Number(e.target.value) : undefined,
                      });
                    }
                  }}
                  fullWidth
                  onlyValidInput
                  disableError
                  validator="PositiveInt"
                />
              </div>
            </div>
          )}

          <div className="flex flex-row flex-wrap gap-4">
            <div className="flex-1 min-w-[150px]">
              <label className="block text-sm font-bold mb-1">{t("common:caseCount")}</label>
              <NumberInput
                translate="no"
                value={caseCount ?? undefined}
                onChange={(e) =>
                  updateCargoGroup(id, {
                    case_count: e.target.value ? Number(e.target.value) : undefined,
                  })
                }
                fullWidth
                onlyValidInput
                disableError
                validator="PositiveInt"
              />
            </div>
          </div>

          <div className="flex justify-end mt-2">
            <button
              type="button"
              className="text-red-500 border border-red-500 rounded px-3 py-1 hover:bg-red-50 transition-colors"
              onClick={() => removeCargoGroup(id)}
            >
              {t("common:delete")}
            </button>
          </div>
        </div>
      </Collapse>
    </div>
  );
};

export const FtlCargoGroupFormView: React.FC<PackageGroupsViewProps> = ({
  expanded,
  onExpandChange,
  divProps,
  sectionProps,
}) => {
  const { t } = useTranslation(["common", "shipper"]);

  const { topLevelCargoGroupIds, addCargoGroup } = useContext(CargoGroupContext);

  // For ftl we expect the top level cargo groups to be handling units
  const cargoGroups = useCargoGroupsSliceSelector((state) => selectManyCargoGroups(state, topLevelCargoGroupIds));

  const handleAccordionChange = (idx: number) => {
    if (idx === expanded) {
      onExpandChange(false);
    } else {
      onExpandChange(idx);
    }
  };

  return (
    <section {...sectionProps} className={classNames("mb-6", sectionProps?.className)}>
      <div {...divProps} className={classNames("flex flex-col rounded-md", divProps?.className)}>
        {cargoGroups.map((cargoGroup, index) => (
          <HandlingUnitView
            key={cargoGroup.id ?? index}
            handlingUnit={cargoGroup}
            index={index}
            total={cargoGroups.length}
            handleAccordionChange={handleAccordionChange}
            expanded={expanded}
          />
        ))}

        <Accordion
          onClick={() =>
            addCargoGroup(
              {
                cargo_group_type: CargoGroupType.HANDLING,
                packaging_type: ShipmentPackagingType.Pallets,
                count: 0,
                weight_unit: ShipmentWeightUnit.Lb,
              },
              { isTopLevelCargoGroup: true }
            )
          }
          className={clsx("border border-gray-200 rounded-b-xl", cargoGroups.length === 0 ? "rounded-t-xl" : "")}
        >
          <AccordionSummary>
            <div className="flex items-center gap-4 px-2 focus:outline-none" data-testid="add-another-item">
              <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#0045FF]">
                <AddCircleIcon style={{ fontSize: 40, color: "#EAEEFD" }} />
              </span>
              <span className="font-bold text-md text-gray-900">{t("common:addAnotherItem")}</span>
            </div>
          </AccordionSummary>
        </Accordion>
      </div>
    </section>
  );
};
